import clsx from "clsx";
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector as useReduxSelector } from "react-redux";
import BurgerMenuCountrySelector from "web/react/components/burger-menu/burger-menu-country-selector-menu/burger-menu-country-selector-menu";
import LanguageSelector from "web/react/components/burger-menu/burger-menu-language-selector/burger-menu-language-selector";
import { useKevelCampaignControl } from "web/react/hooks/use-kevel-campaign-control/use-kevel-campaign-control";
import withReduxProvider from "web/react/redux-provider";
import {
    MainNavigation as MainNavigationReduxInterface,
    setSelectedGender,
} from "web/redux/ducks/main-navigation";
import analytics from "web/script/analytics/analytics";
import { LanguageChoicePropInterface } from "web/types/language";
import MainNavigationDesktop from "./main-navigation-desktop/main-navigation-desktop";
import MainNavigationGenderMenu from "./main-navigation-gender-menu/main-navigation-gender-menu";
import SlideContainer from "./main-navigation-slide-container/main-navigation-slide-container";
import MainNavigationSubCategory from "./main-navigation-subcategory/main-navigation-subcategory";
import styles from "./main-navigation.module.css";

interface GenderButtonProps {
    title: string;
    isActive: boolean;
    onGenderClick: () => void;
}

function GenderButton({ title, isActive, onGenderClick }: GenderButtonProps): React.ReactElement {
    return (
        <button
            type="button"
            onClick={(): void => onGenderClick()}
            className={clsx(styles.genderOption, {
                [styles.genderActive]: isActive,
            })}
        >
            {title}
        </button>
    );
}

export interface MainNavigationProps {
    data: any;
    userGender: string;
    showCountryPicker: boolean;
    currentCountry: string;
    currentCurrency: string;
    allCountries: any[];
    languageSelector: {
        is_shown: boolean;
        callout_text: string;
        choices: LanguageChoicePropInterface[];
        num_columns: number;
    };
    featuredDesignersUrl: string;
    fullRender?: boolean;
}

function extractNavigation(state): MainNavigationReduxInterface {
    return state.mainNavigationReducer;
}

export function MainNavigation({
    data,
    userGender,
    showCountryPicker,
    currentCountry,
    currentCurrency,
    allCountries,
    languageSelector,
    fullRender,
    featuredDesignersUrl,
}: MainNavigationProps): React.ReactElement | null {
    const dispatch = useDispatch();
    const { visible, selectedGender } = useReduxSelector(extractNavigation);
    const [hasRendered, setHasRendered] = useState<boolean>(false);
    const [categories, setCategories] = useState<any[]>([]);
    const [menuIndex, setMenuIndex] = useState(0);
    const [renderCountries, setRenderCountries] = useState(false);
    const [renderLanguages, setRenderLanguages] = useState(false);
    const genderMenuArray = [data.F, data.M];
    const COUNTRY_SLIDE = 1;
    const LANGUAGE_SLIDE = 2;
    const genders = {
        women: "F",
        men: "M",
    };
    let isWomenMenu;
    let currentLanguage = languageSelector.choices.find(
        (language) => language.is_selected === true
    );

    const {
        campaignDetails: branchLinkDetails,
        onClick: onBranchLinkClick,
        onLoad,
    } = useKevelCampaignControl("branch_link");

    function scrollToTop(): void {
        const menuContent = document.getElementById("burgerMenuContent");
        if (menuContent !== null) {
            menuContent.scrollTop = 0;
        }
    }

    function showNextCategory(nextCategory): void {
        if (!nextCategory.url) {
            const newCategories = [...categories, nextCategory];
            setCategories(newCategories);
            setMenuIndex(newCategories.length);
            scrollToTop();
            analytics.event("nav", "clicked", `user_click_${nextCategory.name}`);
        }
    }

    function goBackCategory(): void {
        categories.pop();
        setMenuIndex(categories.length);
        setCategories([...categories]);
        scrollToTop();
    }

    function showCountrySlide(): void {
        analytics.event("SETTINGS", "SHOW_COUNTRY_OVERLAY");
        setRenderCountries(true);
        setMenuIndex(COUNTRY_SLIDE);
        scrollToTop();
    }

    function hideCountrySlide(): void {
        setRenderCountries(false);
        setMenuIndex(0);
        scrollToTop();
    }

    function showLanguageSlide(): void {
        setRenderLanguages(true);
        setMenuIndex(LANGUAGE_SLIDE);
        scrollToTop();
    }

    function hideLanguageSlide(): void {
        setRenderLanguages(false);
        setMenuIndex(0);
        scrollToTop();
    }

    // Gender can be set from the request (userGender) or
    // from redux (selectedGender) by interacting with the navigation
    let currentGender = "women"; // default

    if (userGender) {
        currentGender = userGender.toLowerCase();
    }

    if (selectedGender !== "") {
        currentGender = selectedGender;
    }

    isWomenMenu = currentGender === "women";

    if (visible) {
        scrollToTop();
    }

    // Track first render of the navigation
    useEffect(() => {
        if (visible) {
            setHasRendered(true);
        }
    }, [visible]);

    useEffect(() => {
        if (visible) {
            // fire kevel event when menu is visible
            onLoad();
            document.body.classList.add("no-scroll");
        } else {
            document.body.classList.remove("no-scroll");
        }
    });

    // We only prevent rendering of the navigation on initial page load and if the user-agent is not a bot
    // hasRendered -> boolean to indicate if we have already openened the navigation once so we don't remove the elements from the DOM when we close it
    // visible -> coming from our Redux store, controlling the visibility
    // fullRender -> boolean indicating if the page is visited by a bot
    // ORG-3123 changes this logic - we don't fullRender if a bot
    if (!hasRendered && !visible && !fullRender) {
        return null;
    }

    return (
        <div>
            <nav
                id="burgerMenuContent"
                className={clsx(styles.nav, { [styles.navOpen]: visible })}
                aria-label="Lyst navigation menu"
            >
                <SlideContainer index={menuIndex}>
                    <div>
                        <div className={styles.genderSelector}>
                            <GenderButton
                                title={data.F.display_name}
                                isActive={isWomenMenu}
                                onGenderClick={(): void => {
                                    dispatch(setSelectedGender("women"));
                                }}
                            />
                            <GenderButton
                                title={data.M.display_name}
                                isActive={!isWomenMenu}
                                onGenderClick={(): void => {
                                    dispatch(setSelectedGender("men"));
                                }}
                            />
                        </div>

                        <div
                            className={clsx(styles.genderMenuContainer, {
                                [styles.genderMenuSwitch]: !isWomenMenu,
                            })}
                        >
                            {genderMenuArray.map((genderMenu) => (
                                <MainNavigationGenderMenu
                                    key={genderMenu.display_name}
                                    menu={genderMenu}
                                    fullRender={fullRender}
                                    showCountryPicker={showCountryPicker}
                                    onCategoryClick={showNextCategory}
                                    onCountrySelectorClick={showCountrySlide}
                                    currentCountry={currentCountry}
                                    currentCurrency={currentCurrency}
                                    currentLanguage={currentLanguage}
                                    onLanguageSelectorClick={showLanguageSlide}
                                    showLanguageSelector={languageSelector.is_shown}
                                    branchLinkDetails={branchLinkDetails}
                                    onBranchLinkClick={onBranchLinkClick}
                                />
                            ))}
                        </div>
                    </div>

                    {categories &&
                        categories.map((category) => {
                            return (
                                <MainNavigationSubCategory
                                    key={category.display_name}
                                    title={category.display_name}
                                    submenu={category}
                                    show={true}
                                    onCategoryClick={showNextCategory}
                                    onClose={goBackCategory}
                                />
                            );
                        })}

                    {renderCountries && (
                        <BurgerMenuCountrySelector
                            allCountries={allCountries}
                            currentCountry={currentCountry}
                            currentCurrency={currentCurrency}
                            onBackToMainClick={hideCountrySlide}
                        />
                    )}

                    {renderLanguages && (
                        <LanguageSelector
                            allLanguages={languageSelector.choices}
                            onBackToMainClick={hideLanguageSlide}
                        />
                    )}
                </SlideContainer>
            </nav>

            <MainNavigationDesktop
                menuData={data[genders[currentGender]]}
                featuredDesignersUrl={featuredDesignersUrl}
                visible={visible}
                dispatch={dispatch}
            />
        </div>
    );
}

MainNavigation.defaultProps = {
    data: [],
    userGender: "",
    showCountryPicker: false,
    currentCountry: "",
    currentCurrency: "",
    allCountries: [],
    fullRender: false,
};

export default withReduxProvider(MainNavigation);
